import { FormEvent, useMemo, useState } from 'react'
import { Link, useForm, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Textarea } from '@/shared/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { t } from '@/shared/lib/i18n'
import { adminCodeValuePath, adminCodeValuesPath } from '@/shared/lib/routes'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { formatLookupCategoryLabel } from '@/shared/lib/formatLookupCategoryLabel'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import type { SharedProps } from '@/shared/types/SharedProps'
import type { WebAdminCodeValueForm } from '@/shared/types/web'

interface CodeValuesFormPageProps {
  codeValue: WebAdminCodeValueForm | null
  isEdit: boolean
  categories: string[]
}

interface FormErrors {
  [key: string]: string[] | undefined
}

type PageProps = SharedProps & CodeValuesFormPageProps & { errors?: FormErrors }

const FieldError = ({ errors, field }: { errors: FormErrors; field: string }) => {
  const messages = errors[field]
  if (!messages?.length) return null
  return <p className="mt-1 text-sm text-destructive">{messages[0]}</p>
}

const CATEGORY_NONE_VALUE = '__cv_category_none__'

const CodeValuesFormPage = () => {
  const { codeValue, isEdit, categories, errors: pageErrors } = usePage<PageProps>().props

  useBreadcrumbs([
    { label: t('nav.administration') },
    { label: t('nav.codeValues'), href: adminCodeValuesPath() },
    { label: isEdit ? t('admin.codeValues.editTitle') : t('admin.codeValues.newTitle') },
  ])

  const canCreate = useCan(Resources.CODE_VALUES, Actions.CREATE)
  const canUpdate = useCan(Resources.CODE_VALUES, Actions.UPDATE)

  const form = useForm({
    code: codeValue?.code ?? '',
    description: codeValue?.description ?? '',
    category: codeValue?.category ?? '',
    sortOrder: codeValue?.sortOrder != null ? String(codeValue.sortOrder) : '',
  })

  const errors: FormErrors = pageErrors ?? {}

  const mergedCategorySlugs = useMemo(() => {
    const unique = new Set<string>()
    categories.forEach((slug) => unique.add(slug))
    if (form.data.category) {
      unique.add(form.data.category)
    }
    return Array.from(unique).sort((left, right) => left.localeCompare(right))
  }, [categories, form.data.category])

  const [manualCategorySlug, setManualCategorySlug] = useState(() => {
    const slug = codeValue?.category ?? ''
    return Boolean(slug) && !categories.includes(slug)
  })

  const showCategorySelect = mergedCategorySlugs.length > 0 && !manualCategorySlug

  const canSubmit = isEdit ? canUpdate : canCreate

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    form.transform((data) => {
      const rawSort = data.sortOrder.trim()
      const sortOrder =
        rawSort === '' ? null : Number.isFinite(Number(rawSort)) ? Number(rawSort) : rawSort

      return {
        code_value: {
          code: data.code,
          description: data.description || null,
          category: data.category || null,
          sort_order: sortOrder,
        },
      }
    })
    if (isEdit && codeValue?.id) {
      form.patch(adminCodeValuePath(codeValue.id))
    } else {
      form.post(adminCodeValuesPath())
    }
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border p-6">
        <h1 className="mb-6 text-2xl font-bold">
          {isEdit ? t('admin.codeValues.editTitle') : t('admin.codeValues.newTitle')}
        </h1>

        {errors.base && (
          <div className="mb-4 rounded border border-destructive bg-destructive/10 px-4 py-3">
            {errors.base.map((message, index) => (
              <p key={index} className="text-sm text-destructive">
                {message}
              </p>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="max-w-xl space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="code">{t('admin.codeValues.fields.code')} *</Label>
            <Input
              id="code"
              value={form.data.code}
              onChange={(event) => form.setData('code', event.target.value)}
              required
              autoComplete="off"
            />
            <FieldError errors={errors} field="code" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">{t('admin.codeValues.fields.description')}</Label>
            <Textarea
              id="description"
              value={form.data.description ?? ''}
              onChange={(event) => form.setData('description', event.target.value)}
              rows={3}
            />
            <FieldError errors={errors} field="description" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={showCategorySelect ? 'category' : 'category-slug'}>
              {t('admin.codeValues.fields.category')}
            </Label>
            {showCategorySelect ? (
              <Select
                value={form.data.category ? form.data.category : CATEGORY_NONE_VALUE}
                onValueChange={(value) =>
                  form.setData('category', value === CATEGORY_NONE_VALUE ? '' : value)
                }
              >
                <SelectTrigger id="category" className="w-full">
                  <SelectValue placeholder={t('admin.codeValues.categoryPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CATEGORY_NONE_VALUE}>
                    {t('admin.codeValues.categoryNone')}
                  </SelectItem>
                  {mergedCategorySlugs.map((slug) => (
                    <SelectItem key={slug} value={slug}>
                      {formatLookupCategoryLabel(slug)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id="category-slug"
                value={form.data.category ?? ''}
                onChange={(event) => form.setData('category', event.target.value)}
                placeholder={t('admin.codeValues.categorySlugPlaceholder')}
                autoComplete="off"
              />
            )}
            {mergedCategorySlugs.length > 0 && (
              <div>
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 text-sm"
                  onClick={() => setManualCategorySlug((previous) => !previous)}
                >
                  {manualCategorySlug
                    ? t('admin.codeValues.categoryPickFromList')
                    : t('admin.codeValues.categoryManualSlug')}
                </Button>
              </div>
            )}
            <FieldError errors={errors} field="category" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="sortOrder">{t('admin.codeValues.fields.sortOrder')}</Label>
            <Input
              id="sortOrder"
              type="number"
              value={form.data.sortOrder}
              onChange={(event) => form.setData('sortOrder', event.target.value)}
            />
            <FieldError errors={errors} field="sort_order" />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={form.processing || !canSubmit}>
              {isEdit ? t('admin.codeValues.submitUpdate') : t('admin.codeValues.submitCreate')}
            </Button>
            <Button type="button" variant="outline" asChild>
              <Link href={adminCodeValuesPath()}>{t('admin.codeValues.cancelButton')}</Link>
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CodeValuesFormPage
